import { useWaitForImagesToLoad } from "@faire/web--source/common/performance/useWaitForImagesToLoad/useWaitForImagesToLoad";
import { IViewLayout } from "@faire/web-api--source/indigofair/data/IViewLayout";
import React from "react";

import { SDUISectionRenderer } from "@faire/retailer/components/SDUI/__internal__/SDUISectionRenderer/SDUISectionRenderer";
import {
  ComponentRenderMap,
  SDUIUPLTProps,
  SectionRenderMap,
} from "@faire/retailer/components/SDUI/types";
import { SDUIUPLTContext } from "@faire/retailer/components/SDUI/UPLT/SDUIUPLTContext";

interface SDUIRendererProps {
  componentRenderers: ComponentRenderMap;
  sectionRenderers: SectionRenderMap;
  viewLayout: IViewLayout;
  UPLTprops?: SDUIUPLTProps;
  onClickClientDismissHandler?: () => void;
}

const IMAGE_PERCENTAGE_REMAINING = 0.1;
const IMAGE_COUNT_REMAINING = 1;

const IMAGE_LOADING_TIMEOUT = 7_000;

export const SDUIRenderer: React.FC<SDUIRendererProps> = ({
  componentRenderers,
  sectionRenderers,
  viewLayout,
  UPLTprops,
  onClickClientDismissHandler,
}) => {
  const [shouldRenderBelowTheFold, setShouldRenderBelowTheFold] =
    React.useState(UPLTprops?.shouldRenderBelowTheFold);

  const upltNumComponents = React.useContext(SDUIUPLTContext).numComponents;

  const handleRenderBelowTheFold = () => {
    React.startTransition(() => {
      setShouldRenderBelowTheFold(true);
    });
  };

  const waitForImagesToLoadRef = useWaitForImagesToLoad({
    numComponents: upltNumComponents ?? Infinity,
    onImageLoad: (numImagesPending: number, totalNumImages: number) => {
      if (
        numImagesPending < IMAGE_COUNT_REMAINING ||
        numImagesPending / totalNumImages < IMAGE_PERCENTAGE_REMAINING
      ) {
        handleRenderBelowTheFold();
      }
    },
    onTimeout: handleRenderBelowTheFold,
    timeout: IMAGE_LOADING_TIMEOUT,
  });

  const layoutElementRef = !UPLTprops?.shouldRenderBelowTheFold
    ? waitForImagesToLoadRef
    : undefined;

  return (
    <>
      {viewLayout.content.map((section) => {
        return (
          <SDUISectionRenderer
            key={section.id}
            section={section}
            sectionRenderers={sectionRenderers}
            componentRenderers={componentRenderers}
            viewLoggingData={viewLayout.logging_data}
            loadTimingRef={layoutElementRef}
            UPLTprops={UPLTprops}
            shouldRenderBelowTheFold={shouldRenderBelowTheFold}
            onClickClientDismissHandler={onClickClientDismissHandler}
          />
        );
      })}
    </>
  );
};
